/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Row, Label, CustomInput, Col } from 'reactstrap';
import { connect } from 'react-redux';
import { Range } from 'rc-slider';
import 'rc-slider/assets/index.css';

import { RootState } from 'Redux/store';
import Selectors from 'Redux/Selectors';
import Actions from 'Redux/Actions';
import { IProduct } from '@ddot/AddToCartCommon/lib/entities';

import { css } from '@emotion/react';
import Utils from 'Lib/Utils';
import { IGoldPrice } from 'Redux/slices/home/Entities';
import { productListingOptionsIcon, sortDropdownToggle, filterDropdownMenu, filterContainer, sizeItem, purityItem, puritySelectorBtn, puritySelector } from '../Styles/ProductListingStyles';

interface ListingProps {
    uiProductList: IProduct[];
    productList: IProduct[];
    filterProducts(filters: filterParams): void;
    categoryId: string;
    goldPrice: IGoldPrice;
}

const Filter = (props: ListingProps): JSX.Element => {
    const { filterProducts, categoryId, uiProductList, productList, goldPrice } = props;
    const [isOpen, setIsOpen] = useState(false);

    const [activeSize, setActiveSize] = useState<number | undefined>(undefined);
    const [activeLength, setActiveLength] = useState<number | undefined>(undefined);
    const [activePurity, setActivePurity] = useState<string | undefined>(undefined);

    const [purityRange, setPurityRange] = useState<any[]>([]);
    const [weightRange, setWeightRange] = useState<number[]>([]);
    const [lengthRange, setLengthRange] = useState<number[]>([]);
    const [sizeRange, setSizeRange] = useState<number[]>([]);
    const [priceRange, setPriceRange] = useState<number[]>([]);

    const [purity, setPurity] = useState<any[]>([]);
    const [weight, setWeight] = useState<number[]>([]);
    const [length, setLength] = useState<number[]>([]);
    const [size, setSize] = useState<number[]>([]);
    const [price, setPrice] = useState<number[]>([]);

    useEffect(() => {
        setPurity([]);
        setWeight([]);
        setLength([]);
        setSize([]);
        setPrice([]);
    }, [categoryId]);

    useEffect(() => {
        getAvailableFilters();
    }, [uiProductList]);

    const toggle = () => {
        setIsOpen(!isOpen);
    };

    const handlePurityChange = (item: string) => {
        if (item === activePurity) {
            setActivePurity(undefined);
            setPurity([]);
            return;
        }
        setActivePurity(item);
        setPurity([item]);
    };

    // const getAvailableSizes = () => {
    //     let i = sizeRange[0];

    //     while (i <= sizeRange[-1]) {
    //         setSizeRange([...[i]]);

    //         i += 1;
    //     }
    // };

    const handleClickSize = (item: number) => {
        if (item === activeSize) {
            setActiveSize(undefined);
            setSize([]);
            return;
        }

        setActiveSize(item);
        setSize([item, (Math.floor(item) + 0.99)]);
    };

    const handleClickLength = (item: number) => {
        if (item === activeSize) {
            setActiveLength(undefined);
            setLength([]);
            return;
        }

        setActiveLength(item);
        setLength([item, (Math.floor(item) + 0.99)]);
    };

    const getAvailableFilters = () => {
        let productsToBeFiltered = uiProductList;

        if (!productsToBeFiltered.length) productsToBeFiltered = productList;

        if (productsToBeFiltered) {
            let allPurity: string[] = [];
            let allWeight: number[] = [];
            let allLength: number[] = [];
            let allSize: number[] = [];
            let allPrice: number[] = [];

            productsToBeFiltered.forEach(product => {
                if (product.price) {
                    const { minPrice, maxPrice } = Utils.Jemisys.getItemPriceRange(product, goldPrice);

                    if (maxPrice !== minPrice) {
                        allPrice = allPrice.concat(minPrice, maxPrice);
                    } else allPrice = allPrice.concat(maxPrice);
                }

                product.filterAttributes?.forEach(attribute => {
                    if (attribute.name === 'Purity') {
                        const { value } = attribute;

                        if (value.length) {
                            const convertedPurity = value.map(item => item.toString());
                            allPurity = allPurity.concat(convertedPurity);
                        } return false;
                    } if (attribute.name === 'Weight') {
                        const { value } = attribute;

                        if (value.length) {
                            const convertedWeight = value.map(item => Number(item));
                            allWeight = allWeight.concat(convertedWeight);
                        } return false;
                    } if (attribute.name === 'Length') {
                        const { value } = attribute;

                        if (value.length) {
                            const convertedLength = value.map(item => Number(item));
                            allLength = allLength.concat(convertedLength);
                        } return false;
                    } if (attribute.name === 'Size') {
                        const { value } = attribute;

                        if (value.length) {
                            const convertedSize = value.map(item => Number(item));
                            allSize = allSize.concat(convertedSize);
                        } return false;
                    } return false;
                });
            });

            if (allPrice) {
                const sortedPrice = [...new Set(allPrice)].sort().filter(item => !Number.isNaN(item));
                if (sortedPrice.length) {
                    const minPrice = Math.min(...sortedPrice);
                    const maxPrice = Math.max(...sortedPrice);

                    if (minPrice !== maxPrice) setPriceRange([0, maxPrice]);
                }
            }

            if (allPurity) {
                const sortedPurities = [...new Set(allPurity)].sort().filter(item => item !== '-');

                if (sortedPurities.length) setPurityRange(sortedPurities);
            }

            if (allWeight) {
                const sortedWeights = [...new Set(allWeight)].sort().filter(item => !Number.isNaN(item));
                if (sortedWeights.length) {
                    const minWeight = Math.min(...sortedWeights);
                    const maxWeight = Math.max(...sortedWeights);

                    if (minWeight !== maxWeight) setWeightRange([minWeight, maxWeight]);
                }
            }

            if (allLength) {
                const sortedLength = [...new Set(allLength)].sort().filter(item => !Number.isNaN(item));
                if (sortedLength.length) {
                    const minLength = Math.min(...sortedLength);
                    const maxLength = Math.max(...sortedLength);

                    // if (minLength !== 0 && maxLength !== 0) {
                    //     setLengthRange(sortedLength);
                    // }

                    if (minLength !== maxLength) setLengthRange([minLength, maxLength]);
                }
            }

            if (allSize) {
                const sortedSize = [...new Set(allSize)].filter(num => num !== 0).slice().sort((a, b) => a - b);
                if (sortedSize.length) {
                    const minSize = Math.min(...sortedSize);
                    const maxSize = Math.max(...sortedSize);

                    if (minSize !== maxSize) setSizeRange(sortedSize);

                    // getAvailableSizes();
                }
            }
        }
    };

    const renderPurityCheckboxes = () => {
        if (!purityRange) return <Label> - </Label>;

        return purityRange.map((item: string, index: number) => {
            let name = '';

            switch (item) {
                case '999.9G':
                    name = '999.9';
                    break;
                case '999G':
                    name = '999';
                    break;
                case '916G':
                    name = '916(A)';
                    break;
                case '916G(B)':
                    name = '916(B)';
                    break;

                default:
                    return null;
            }
            return (
                <div
                    role='button'
                    tabIndex={0}
                    onClick={() => handlePurityChange(item)}
                    onKeyPress={() => handlePurityChange(item)}
                    style={{ cursor: 'pointer' }}
                    css={puritySelectorBtn}
                >
                    <img
                        className={`${item === activePurity ? 'selected' : ''}`}
                        src='/assets/icons/Ellipse1.svg'
                        alt=''
                        style={{ objectFit: 'contain', borderRadius: 50, padding: 5 }}
                        css={puritySelector}
                    />
                    <p>{name}</p>
                </div>
            );
        });
    };

    useEffect(() => {
        const productFilters: filterParams = {
            filters: [
                {
                    name: 'Purity',
                    type: 'ENUM',
                    valuesSelected: purity,
                },
                {
                    name: 'Weight',
                    type: 'RANGE',
                    min: weight[0],
                    max: weight[1],
                },
                {
                    name: 'Length',
                    type: 'RANGE',
                    min: length[0],
                    max: length[1],
                },
                {
                    name: 'Size',
                    type: 'RANGE',
                    min: size[0],
                    max: size[1],
                },
                {
                    name: 'Price',
                    type: 'RANGE',
                    min: price[0],
                    max: price[1],
                },
            ],
        };

        filterProducts(productFilters);
    }, [purity, weight, length, size, price, uiProductList]);

    // Ref for the filterDropdown because it is a div element and will require extra functions to blur when clicked outside
    const filterDropdownRef = useRef<HTMLDivElement>(null);
    const filterDropdownButtonRef = useRef<HTMLButtonElement>(null);

    useLayoutEffect(() => {
        const handleClickOutside = (event: any) => {
            // Because the filterDropdownRef does not include the filter button so the button is an exception when checking for clicks (button ALSO toggles the dropdown)
            if (filterDropdownRef.current && !filterDropdownRef.current?.contains(event.target) && !filterDropdownButtonRef.current?.contains(event.target)) {
                setIsOpen(false);
            }
        };

        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [filterDropdownRef]);

    const renderFilterMenu = () => {
        if (!isOpen) return false;

        return (
            // className='filter'
            <div css={filterContainer}>
                <div className='itemContainer'>
                    <p style={{ margin: 0 }}>Purity</p>
                    <div css={purityItem}>
                        {renderPurityCheckboxes()}
                    </div>
                </div>

                <div hidden={!priceRange.length} className='rangeContainer'>
                    <p style={{ margin: 0 }}>Price Range</p>
                    <div className='rangeItem'>
                        <span style={{ marginRight: '15px' }}>{price[0]?.toFixed(2) ? price[0]?.toFixed(2) : priceRange[0]?.toFixed(2)}</span>
                        <Range
                            disabled={!priceRange.length}
                            min={priceRange[0]}
                            max={priceRange[1]}
                            defaultValue={price.length ? price : priceRange}
                            onAfterChange={(value) => setPrice(value)}
                            step={0.01}
                            allowCross={false}
                            trackStyle={[{ backgroundColor: '#064E72' }]}
                            handleStyle={[{ backgroundColor: '#064E72', borderColor: '#064E72' }, { backgroundColor: '#064E72', borderColor: '#064E72' }]}
                            maximumTrackStyle={{ backgroundColor: '#8CB5C9' }}
                        />
                        <span style={{ marginLeft: '15px' }}>{price[1]?.toFixed(2) ? price[1]?.toFixed(2) : priceRange[1]?.toFixed(2)}</span>
                    </div>
                </div>

                <div hidden={!sizeRange.length} className='itemContainer'>
                    <p style={{ margin: 0 }}>Size</p>
                    <div css={sizeItem} style={{ display: 'grid', gridTemplateColumns: 'auto auto auto auto auto', gap: 10 }}>
                        {
                            sizeRange.map((item) => (
                                <div
                                    key={item}
                                    className={`${item === activeSize ? 'selected' : ''}`}
                                    role='button'
                                    tabIndex={0}
                                    onClick={() => handleClickSize(item)}
                                    onKeyPress={() => handleClickSize(item)}
                                >
                                    <p>{item}</p>
                                </div>
                            ))
                        }
                    </div>
                </div>

                <div hidden={!weightRange.length} className='rangeContainer'>
                    <p style={{ margin: 0 }}>Weight</p>
                    <div hidden={!weightRange.length} className='rangeItem'>
                        <span style={{ marginRight: '15px' }}>{weight[0]?.toFixed(2) ? weight[0]?.toFixed(2) : weightRange[0]?.toFixed(2)}</span>
                        <Range
                            disabled={!weightRange.length}
                            min={weightRange[0]}
                            max={weightRange[1]}
                            defaultValue={weight.length ? weight : weightRange}
                            onAfterChange={(value) => setWeight(value)}
                            step={0.01}
                            allowCross={false}
                            trackStyle={[{ backgroundColor: '#064E72' }]}
                            handleStyle={[{ backgroundColor: '#064E72', borderColor: '#064E72' }, { backgroundColor: '#064E72', borderColor: '#064E72' }]}
                            maximumTrackStyle={{ backgroundColor: '#8CB5C9' }}
                        />
                        <span style={{ marginLeft: '15px' }}>{weight[1]?.toFixed(2) ? weight[1]?.toFixed(2) : weightRange[1]?.toFixed(2)}</span>
                    </div>
                </div>

                <div hidden={!lengthRange.length} className='itemContainer'>
                    <p style={{ margin: 0 }}>Length</p>
                    <div css={sizeItem}>
                        {
                            lengthRange.map((item) => (
                                <div
                                    className={`${item === activeLength ? 'selected' : ''}`}
                                    role='button'
                                    tabIndex={0}
                                    onClick={() => handleClickLength(item)}
                                    onKeyPress={() => handleClickLength(item)}
                                >
                                    <p>{item}</p>
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={{ width: '100%' }}>
            {/* <Row style={{ alignItems: 'start' }}> */}
            {/* <Col> */}
            <div style={{ borderTop: '1px solid #E6DFD8', borderBottom: '1px solid #E6DFD8', paddingLeft: 5, marginBottom: '5%', marginTop: '5%' }}>
                {renderFilterMenu()}
            </div>
            {/* </Col> */}

            <button type='button' ref={filterDropdownButtonRef} onClick={() => toggle()} css={sortDropdownToggle}>
                <div>
                    <img
                        css={productListingOptionsIcon}
                        src='/assets/icons/filter.svg'
                        alt='Funnel'
                    />
                    Filter
                </div>
            </button>
            {/* </Row> */}
        </div>
    );
};

interface filterParams {
    filters: {
        name: 'Purity' | 'Weight' | 'Length' | 'Size' | 'Price',
        type: 'ENUM' | 'RANGE',
        valuesSelected?: string[],
        min?: number,
        max?: number,
    }[]
}

const mapStateToProps = (state: RootState) => ({
    uiProductList: Selectors.getUiProductList(state),
    productList: Selectors.getProductsList(state),
    goldPrice: Selectors.getHomeGoldPrice(state),
});

const mapDispatchToProps = (dispatch: any) => ({
    filterProducts: (filters: filterParams) => dispatch(Actions.uiFilterProducts(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Filter);
